// BaseCalendarEventService.js

import { collection, getDocs, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { startOfDay } from 'date-fns';

import { collections, db } from 'firebaseConfig';
import { listNodesByKeys } from 'services/nodes/NodeReadService';
import {
  isDatePrecision,
  toDatePrecisionLocalDay,
} from 'utilities/DateTime';
import {
  compareCalendarEventItems,
  isCalendarHiddenNodeType,
} from 'views/calendar/shared/calendarUtils';

const DEFAULT_MAX_EVENTS = 2000;
const RANGE_LOOKBACK_DAYS = 90;

const toMillis = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.round(numeric) : 0;
};

const createDateFromMillis = (value) => {
  const ms = toMillis(value);
  if (!ms) return null;
  const date = new Date(ms);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toPositiveInteger = (value) => {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
};

const createAnnualDateFromRow = (row) => {
  const annualDateValue = row?.annualDateValue;
  const occurrenceYear = toPositiveInteger(row?.occurrenceYear);
  const month = toPositiveInteger(annualDateValue?.month);
  const day = toPositiveInteger(annualDateValue?.day);

  if (!occurrenceYear || !month || !day) return null;

  const date = new Date(occurrenceYear, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const createEventDateFromRow = (row) => {
  if (String(row?.fieldType || '').toLowerCase() === 'annualdate') {
    return createAnnualDateFromRow(row) || createDateFromMillis(row?.startMs);
  }

  const date = createDateFromMillis(row?.startMs);
  if (!date) return null;

  if (isDatePrecision(row?.startPrecision)) {
    return toDatePrecisionLocalDay(date) || date;
  }

  return date;
};

const createEventEndDateFromRow = (row, fallbackDate) => {
  const date = createDateFromMillis(row?.endMs) || fallbackDate;
  if (!date) return null;

  if (isDatePrecision(row?.endPrecision || row?.startPrecision)) {
    return toDatePrecisionLocalDay(date) || date;
  }

  return date;
};

const normalizeEventRows = (snapshot) => snapshot.docs.map((docSnap) => ({
  id: docSnap.id,
  key: docSnap.id,
  ...(docSnap.data() || {}),
}));

const eventOverlapsWindow = (row, windowStartMs, windowEndMs) => {
  if (String(row?.fieldType || '').toLowerCase() === 'annualdate') {
    const eventDate = createAnnualDateFromRow(row);
    if (!eventDate) return false;

    const startMs = startOfDay(eventDate).getTime();
    const endMs = startMs;
    return startMs <= windowEndMs && endMs >= windowStartMs;
  }

  const startMs = toMillis(row?.startMs);
  const endMsRaw = toMillis(row?.endMs);
  const endMs = endMsRaw || startMs;

  if (!startMs) return false;
  return startMs <= windowEndMs && endMs >= windowStartMs;
};

const buildBaseEventsQuery = ({ baseKey, lookbackStartMs, safeEndMs, maxEvents }) => query(
  collection(db, collections.events),
  where('baseKey', '==', baseKey),
  where('startMs', '>=', lookbackStartMs),
  where('startMs', '<=', safeEndMs),
  orderBy('startMs', 'asc'),
  limit(Math.max(1, Number(maxEvents) || DEFAULT_MAX_EVENTS)),
);

const getCalendarFieldRawValueFromNode = (node, fieldKey) => {
  if (!node || !fieldKey) return null;

  const typeKey = String(node?.type || '').trim().toLowerCase();
  if (!typeKey) return null;

  return node?.[`data_${typeKey}`]?.[fieldKey] ?? null;
};

const buildBaseCalendarEventItems = async (rows = []) => {
  if (!rows.length) return [];

  // Schedule and other multi-type calendars should not surface COMMENT noise.
  const visibleRows = rows.filter(
    (row) => !isCalendarHiddenNodeType(row?.nodeType)
  );
  if (!visibleRows.length) return [];

  const nodeKeys = Array.from(new Set(
    visibleRows
      .map((row) => String(row?.nodeKey || '').trim())
      .filter(Boolean)
  ));

  if (!nodeKeys.length) return [];

  const nodes = await listNodesByKeys(nodeKeys);
  const nodeByKey = new Map(
    (Array.isArray(nodes) ? nodes : [])
      .filter((node) => node?.key)
      .map((node) => [node.key, node])
  );

  return visibleRows
    .map((row) => {
      const nodeKey = String(row?.nodeKey || '').trim();
      const node = nodeByKey.get(nodeKey);
      if (!node) return null;

      const eventDate = createEventDateFromRow(row);
      if (!eventDate) return null;

      const fieldType = String(row?.fieldType || '').toLowerCase();
      const fieldKey = row?.fieldKey || '';
      const rawValue = fieldType === 'annualdate'
        ? (row?.annualDateValue || getCalendarFieldRawValueFromNode(node, fieldKey))
        : getCalendarFieldRawValueFromNode(node, fieldKey);

      const endDate = createEventEndDateFromRow(row, eventDate);

      return {
        node,
        field: {
          name: fieldKey,
          label: row?.fieldLabel || '',
          type: fieldType,
        },
        rawValue,
        date: eventDate,
        endDate,
        day: startOfDay(eventDate),
        startPrecision: row?.startPrecision || null,
        endPrecision: row?.endPrecision || null,
        isRange: Boolean(row?.isRange),
        isRecurringOccurrence: Boolean(row?.isRecurringOccurrence),
        occurrenceDateKey: row?.occurrenceDateKey || null,
        seriesNodeKey: row?.seriesNodeKey || nodeKey,
        eventDocId: row?.id || row?.key || null,
      };
    })
    .filter(Boolean)
    .sort(compareCalendarEventItems);
};

export async function loadBaseCalendarEventItems({
  baseKey,
  windowStartMs,
  windowEndMs,
  maxEvents = DEFAULT_MAX_EVENTS,
} = {}) {
  const cleanedBaseKey = String(baseKey || '').trim();
  const safeStartMs = toMillis(windowStartMs);
  const safeEndMs = toMillis(windowEndMs);

  if (!cleanedBaseKey || !safeStartMs || !safeEndMs || safeEndMs < safeStartMs) {
    return [];
  }

  const lookbackStartMs = safeStartMs - RANGE_LOOKBACK_DAYS * 24 * 60 * 60 * 1000;

  const eventsQuery = buildBaseEventsQuery({
    baseKey: cleanedBaseKey,
    lookbackStartMs,
    maxEvents,
    safeEndMs,
  });

  const snapshot = await getDocs(eventsQuery);
  const rows = normalizeEventRows(snapshot);
  const windowRows = rows.filter((row) => eventOverlapsWindow(row, safeStartMs, safeEndMs));

  return buildBaseCalendarEventItems(windowRows);
}

export function subscribeBaseCalendarEventItems({
  baseKey,
  windowStartMs,
  windowEndMs,
  maxEvents = DEFAULT_MAX_EVENTS,
} = {}, callback) {
  if (typeof callback !== 'function') {
    throw new Error('subscribeBaseCalendarEventItems: callback must be a function');
  }

  const cleanedBaseKey = String(baseKey || '').trim();
  const safeStartMs = toMillis(windowStartMs);
  const safeEndMs = toMillis(windowEndMs);

  if (!cleanedBaseKey || !safeStartMs || !safeEndMs || safeEndMs < safeStartMs) {
    callback([]);
    return () => {};
  }

  const lookbackStartMs = safeStartMs - RANGE_LOOKBACK_DAYS * 24 * 60 * 60 * 1000;
  const eventsQuery = buildBaseEventsQuery({
    baseKey: cleanedBaseKey,
    lookbackStartMs,
    maxEvents,
    safeEndMs,
  });

  let requestId = 0;

  return onSnapshot(
    eventsQuery,
    async (snapshot) => {
      const currentRequestId = ++requestId;

      try {
        const rows = normalizeEventRows(snapshot);
        const windowRows = rows.filter((row) => eventOverlapsWindow(row, safeStartMs, safeEndMs));
        const items = await buildBaseCalendarEventItems(windowRows);
        if (currentRequestId !== requestId) return;
        callback(items);
      } catch (error) {
        console.error('[BaseCalendarEventService] subscribeBaseCalendarEventItems error:', error);
        if (currentRequestId !== requestId) return;
        callback([]);
      }
    },
    (error) => {
      console.error('[BaseCalendarEventService] subscribeBaseCalendarEventItems snapshot error:', error);
      callback([]);
    }
  );
}
